/**
 * Admin Moderation — list of reports + actions (resolve, hide content, restore).
 * Hidden under /admin/moderation. Requires admin auth.
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Flag, EyeOff, Eye, CheckCircle2, RefreshCw, ChevronDown } from 'lucide-react';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_TABS = [
  { id: 'open',     label: 'Open' },
  { id: 'resolved', label: 'Resolved' },
  { id: 'all',      label: 'All' },
];

const REASON_LABELS = {
  spam: 'Spam',
  abuse: 'Abuse',
  fake_catch: 'Fake catch',
  offensive: 'Offensive',
  other: 'Other',
};

export default function AdminModeration() {
  const navigate = useNavigate();
  const token = localStorage.getItem('carpcast_token');
  const [status, setStatus] = useState('open');
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [noteByRow, setNoteByRow] = useState({});

  const setNote = (id, v) => setNoteByRow((p) => ({ ...p, [id]: v }));

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/reports?status=${status}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setReports(r.data);
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error('Admins only');
        navigate('/dashboard', { replace: true });
        return;
      }
      toast.error('Could not load reports');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const resolve = async (id) => {
    try {
      await axios.post(`${API}/admin/reports/${id}/resolve`, null, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Report dismissed');
      load();
    } catch {
      toast.error('Failed to dismiss');
    }
  };

  const hide = async (contentType, contentId, note) => {
    try {
      await axios.post(`${API}/admin/content/${contentType}/${contentId}/hide`, { note: note || null }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Content hidden');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to hide');
    }
  };

  const restore = async (contentType, contentId) => {
    try {
      await axios.post(`${API}/admin/content/${contentType}/${contentId}/restore`, null, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Content restored');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to restore');
    }
  };

  return (
    <div className="min-h-screen bg-[#0b0d0b] px-4 py-8 md:py-12">
      <div className="max-w-5xl mx-auto" data-testid="admin-moderation">
        <div className="flex items-center justify-between mb-6">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-[#c8b273] mb-1">
              <Flag className="inline-block w-3 h-3 mr-1.5 -mt-0.5" />
              Admin · Moderation
            </p>
            <h1 className="font-['Barlow_Condensed'] text-3xl md:text-4xl font-extrabold uppercase tracking-wider text-white">
              Reports queue
            </h1>
          </div>
          <button
            onClick={load}
            className="text-[#8a948a] hover:text-[#c8b273] p-2"
            data-testid="admin-mod-refresh-btn"
            aria-label="Refresh"
          >
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        <div className="flex gap-2 mb-4 border-b border-[#1f221f]">
          {STATUS_TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setStatus(t.id)}
              className={`px-4 py-2 text-sm font-bold uppercase tracking-wider transition-colors ${
                status === t.id
                  ? 'text-[#c8b273] border-b-2 border-[#c8b273]'
                  : 'text-[#8a948a] hover:text-white'
              }`}
              data-testid={`admin-mod-tab-${t.id}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {reports.length === 0 ? (
          <div className="glass-card p-12 text-center" data-testid="admin-mod-empty">
            <Flag className="w-10 h-10 text-[#4a504a] mx-auto mb-3" />
            <p className="text-white font-semibold">No reports in this queue</p>
            <p className="text-[#8a948a] text-sm mt-1">
              {status === 'open' ? 'Everything is clean — nice work.' : 'Nothing matches this filter yet.'}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {reports.map((r) => (
              <div key={r.id} className="glass-card p-4" data-testid={`report-row-${r.id}`}>
                <button
                  onClick={() => setExpanded(expanded === r.id ? null : r.id)}
                  className="w-full text-left flex items-start justify-between gap-4"
                  data-testid={`report-row-toggle-${r.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-red-500/10 text-red-400 border border-red-500/20">
                        {REASON_LABELS[r.reason] || r.reason}
                      </span>
                      <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-[#1f221f] text-[#c8b273]">
                        {r.content_type}
                      </span>
                      {r.status === 'resolved' && (
                        <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-green-500/10 text-green-400 border border-green-500/20">
                          {r.resolution_action || 'resolved'}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-white truncate">
                      {r.reporter_username || 'Anonymous'} reported <span className="text-[#c8b273] font-mono">{r.content_id.slice(0, 8)}</span>
                    </p>
                    <p className="text-xs text-[#8a948a] mt-0.5">
                      {new Date(r.created_at).toLocaleString()}
                    </p>
                  </div>
                  <ChevronDown className={`w-5 h-5 text-[#8a948a] flex-shrink-0 transition-transform ${expanded === r.id ? 'rotate-180' : ''}`} />
                </button>

                {expanded === r.id && (
                  <div className="mt-4 pt-4 border-t border-[#1f221f] space-y-3">
                    {r.description && (
                      <div>
                        <p className="text-[10px] uppercase tracking-wider text-[#8a948a] mb-1">Reporter note</p>
                        <p className="text-sm text-[#c0c4c0] bg-[#0b0d0b] border border-[#1f221f] rounded p-2.5">{r.description}</p>
                      </div>
                    )}
                    <div>
                      <p className="text-[10px] uppercase tracking-wider text-[#8a948a] mb-1">Content ID</p>
                      <p className="text-xs font-mono text-[#c8b273] break-all">{r.content_id}</p>
                    </div>
                    {r.status === 'open' ? (
                      <div className="space-y-2 pt-2">
                        <textarea
                          rows={2}
                          maxLength={500}
                          value={noteByRow[r.id] || ''}
                          onChange={(e) => setNote(r.id, e.target.value)}
                          placeholder="Optional admin note (visible in audit log)"
                          className="w-full bg-[#0b0d0b] border border-[#1f221f] rounded p-2 text-xs text-white placeholder-[#4a504a] focus:border-[#c8b273] outline-none"
                          data-testid={`report-note-${r.id}`}
                        />
                        <div className="flex flex-wrap gap-2">
                          <Button
                            onClick={() => hide(r.content_type, r.content_id, noteByRow[r.id])}
                            variant="destructive"
                            size="sm"
                            data-testid={`report-hide-btn-${r.id}`}
                          >
                            <EyeOff className="w-4 h-4 mr-1.5" />
                            Hide content
                          </Button>
                          <Button
                            onClick={() => resolve(r.id)}
                            variant="ghost"
                            size="sm"
                            data-testid={`report-dismiss-btn-${r.id}`}
                          >
                            <CheckCircle2 className="w-4 h-4 mr-1.5" />
                            Dismiss
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-wrap gap-2 pt-2">
                        <Button
                          onClick={() => restore(r.content_type, r.content_id)}
                          variant="outline"
                          size="sm"
                          data-testid={`report-restore-btn-${r.id}`}
                        >
                          <Eye className="w-4 h-4 mr-1.5" />
                          Restore content
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
